// @ts-expect-error - Import needs esModuleInterop flag
import React, { useState, ReactElement } from 'react'
import { Controller, Control } from 'react-hook-form'

import { FormControl, TextField, TextFieldProps } from '@mui/material'
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { enUS, fr, de, it, es, nl, pt } from 'date-fns/locale'
// @ts-expect-error - Import needs esModuleInterop flag
import { useTranslation } from '@contexts/translation'

// Import locale type from date-fns
import type { Locale } from 'date-fns'

// Define locale map with locale information
interface LocaleInfo {
  locale: Locale
}

// Type for renderInput function to fix type error
type RenderInputFn = (params: any) => ReactElement

interface TimePickerProps {
  /** Field name in the form */
  name: string
  /** Label text for the field */
  label?: string
  /** React Hook Form control object */
  control: Control<any>
  /** Whether the field has an error */
  error?: boolean
  /** ID for the field */
  id?: string
  /** Error message to display */
  errorMessage?: string
  /** Whether the field takes up the full width */
  fullWidth?: boolean
  /** Whether the field is required */
  required?: boolean
  /** Whether the field is disabled */
  disabled?: boolean
  /** Whether the field can be cleared */
  clearable?: boolean
  /** Custom width for the field */
  width?: string | number
  /** Margin size */
  margin?: 'none' | 'dense' | 'normal'
  /** Form control size */
  size?: 'small' | 'medium'
  /** Text field size */
  textFieldSize?: 'small' | 'medium'
  /** Time input format */
  inputFormat?: string
  /** Props to pass to the input component */
  InputProps?: Partial<TextFieldProps['InputProps']>
  /** Additional props */
  [key: string]: any
}

/**
 * TimePicker component
 * A form time picker component that works with React Hook Form
 */
const TimePicker = ({
  name,
  label,
  control,
  error,
  id,
  errorMessage,
  fullWidth,
  required,
  disabled,
  clearable,
  width,
  margin = 'normal',
  size = 'medium',
  textFieldSize = 'small',
  inputFormat = 'HH:mm',
  InputProps = {},
  ...props
}: TimePickerProps): JSX.Element => {
  const [value, setValue] = useState<Date | null>(null)
  const { lang } = useTranslation()

  const localeMap: Record<string, LocaleInfo> = {
    en: { locale: enUS },
    fr: { locale: fr },
    de: { locale: de },
    it: { locale: it },
    es: { locale: es },
    nl: { locale: nl },
    pt: { locale: pt }
  }

  return (
    <FormControl
      {...props}
      margin={margin}
      size={size}
      style={{ width: fullWidth ? '100%' : width || '195px' }}
    >
      <Controller
        name={name}
        control={control}
        defaultValue={null}
        render={({ field }) => (
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            locale={localeMap[lang]?.locale}
          >
            <MuiTimePicker
              value={value}
              disabled={disabled}
              inputFormat={inputFormat}
              onChange={(newValue: Date | null) => {
                setValue(newValue)
              }}
              {...field}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  label={label}
                  id={id}
                  disabled={disabled}
                  name={name}
                  error={error}
                  helperText={errorMessage}
                  required={required}
                  InputLabelProps={{
                    shrink: true
                  }}
                  fullWidth
                  size={textFieldSize}
                  style={{ backgroundColor: '#fff' }}
                  {...InputProps}
                />
              )}
            />
          </LocalizationProvider>
        )}
      />
    </FormControl>
  )
}

export default TimePicker
